import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import playstore from "../assets/images/playstore.png";
import appstore from "../assets/images/appstore.png";
import instagram from "../assets/images/instagram.png";
import {
  FaChevronLeft,
  FaDiscord,
  FaExternalLinkAlt,
  FaGamepad,
  FaStar,
} from "react-icons/fa";
import Logo from "../components/site/Logo";
import SiteBackdrop from "../components/site/SiteBackdrop";
import { Badge } from "../ui";
import {
  APP_STORE_URL,
  INSTAGRAM_URL,
  PLAY_STORE_URL,
} from "../constants/links";

const rowClass =
  "group flex items-center gap-4 rounded-2xl border p-4 md:p-5 text-white " +
  "transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5 " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-header";

// One big link-in-bio row. Internal links stay in the app, others open a tab.
const LinkRow = ({ link }) => {
  const tone = link.featured
    ? "bg-mainButton/15 border-mainButton/40 hover:bg-mainButton/25 hover:border-mainButton/60"
    : "bg-white/[0.06] border-white/10 hover:bg-white/[0.1] hover:border-white/20";
  const content = (
    <>
      <span
        className={`w-14 h-14 shrink-0 rounded-xl flex items-center justify-center ${
          link.featured ? "bg-mainButton text-white" : "bg-white/10"
        }`}
      >
        {link.icon}
      </span>
      <span className="flex-1 min-w-0 text-right">
        <span className="font-bold text-lg flex items-center gap-2">
          {link.title}
          {link.isUnique && <Badge tone="copper">جديد</Badge>}
        </span>
        <span className="block text-sm text-white/70">{link.subtitle}</span>
      </span>
      {link.isInternal ? (
        <FaChevronLeft
          className="shrink-0 text-white/55 transition-transform group-hover:-translate-x-1 group-hover:text-white"
          aria-hidden="true"
        />
      ) : (
        <FaExternalLinkAlt
          className="shrink-0 text-sm text-white/55 group-hover:text-white"
          aria-hidden="true"
        />
      )}
    </>
  );

  if (link.isInternal) {
    return (
      <Link to={link.url} className={`${rowClass} ${tone}`}>
        {content}
      </Link>
    );
  }
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`${rowClass} ${tone}`}
    >
      {content}
    </a>
  );
};

function Portfolio() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const links = [
    {
      id: 1,
      title: "انضم إلى مجتمع تربع",
      subtitle: "Discord - خوادمنا على ديسكورد",
      icon: <FaDiscord className="text-3xl" aria-hidden="true" />,
      url: "https://discord.gg/drNmdDuRF3",
      isUnique: true,
      platform: "community",
    },

    {
      id: 4,
      title: "ترَبَّع - أبستور",
      subtitle: "iOS - App Store",
      icon: <img src={appstore} alt="" className="w-8 h-8" />,
      url: APP_STORE_URL,
      platform: "ios",
    },
    {
      id: 5,
      title: "ترَبَّع - قوقل بلاي",
      subtitle: "Android - Google Play",
      icon: <img src={playstore} alt="" className="w-8 h-8" />,
      url: PLAY_STORE_URL,
      platform: "android",
    },
    {
      id: 6,
      title: "العب بالموقع",
      subtitle: "ابدأ اللعب الآن",
      icon: <FaGamepad className="text-2xl" aria-hidden="true" />,
      url: "/",
      isInternal: true,
      featured: true,
      platform: "web",
    },
    {
      id: 7,
      title: "تابعنا على الإنستغرام",
      subtitle: "tarbaakw@",
      icon: <img src={instagram} alt="" className="w-8 h-8" />,
      url: INSTAGRAM_URL,
      platform: "social",
    },
  ];

  return (
    <div
      className="relative isolate min-h-screen overflow-hidden bg-header font-sans text-white"
      dir="rtl"
    >
      <SiteBackdrop />

      {/* Content */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4 py-12">
        {/* Logo and title */}
        <div
          className={`text-center mb-10 transition-all duration-700 motion-reduce:transition-none ${
            isVisible
              ? "translate-y-0 opacity-100"
              : "-translate-y-6 opacity-0"
          }`}
        >
          <Logo className="w-36 h-36 md:w-48 md:h-48 mx-auto mb-6 drop-shadow-2xl" />
          <h1 className="text-3xl md:text-4xl font-extrabold leading-tight">
            تربع - روابط التطبيق
          </h1>
          <p className="text-white/70 text-lg mt-3 max-w-md mx-auto leading-relaxed">
            حمّل تطبيق تربع واستمتع بأفضل لعبة عربية للعائلة والأصدقاء
          </p>
          <div
            className="flex items-center justify-center gap-1 mt-4 text-primary"
            aria-hidden="true"
          >
            {[...Array(5)].map((_, i) => (
              <FaStar key={i} className="text-lg" />
            ))}
          </div>
        </div>

        {/* Links */}
        <nav aria-label="روابط تربع" className="w-full max-w-lg space-y-3">
          {links.map((link, index) => (
            <div
              key={link.id}
              className={`transition-all duration-500 motion-reduce:transition-none ${
                isVisible
                  ? "translate-y-0 opacity-100"
                  : "translate-y-6 opacity-0"
              }`}
              style={{ transitionDelay: `${index * 0.08}s` }}
            >
              <LinkRow link={link} />
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div
          className={`mt-12 text-center text-white/55 transition-opacity duration-700 ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
          style={{ transitionDelay: "0.5s" }}
        >
          <p className="text-sm">
            © {new Date().getFullYear()} تربع - جميع الحقوق محفوظة
          </p>
        </div>
      </div>
    </div>
  );
}

export default Portfolio;
